<template>
    <div v-if="selectedPublisherModel">
        <LaBaseSelect
            data-test="ltba-publishers-select"
            :items="ltbaPublishers"
            v-model="selectedPublisherModel"
            itemValue="id"
            itemText="name"
            :stretch="false"
            :searchable="true"
            v-if="ltbaPublishers.length > 0"
        >
        </LaBaseSelect>
    </div>
</template>

<script>
import LaBaseSelect from "@/components/inputs/BaseSelect";
import { computed, watch } from "vue";
import store from "@/store/store";
import { actionTypes, getterTypes } from "@/store/Publisher/types";
import { useRouter, useRoute } from "vue-router";

export default {
    name: "LtbaPublisherPicker",
    components: {
        LaBaseSelect,
    },
    setup() {
        const router = useRouter();
        const route = useRoute();

        // Get all publishers
        const publishers = computed(() => store.getters[getterTypes.getPublishers]);
        if (!publishers.value) {
            store.dispatch(actionTypes.getData);
        }

        // Filter only LTBA publishers (is_yield = false)
        const ltbaPublishers = computed(() => {
            if (!publishers.value) return [];
            return publishers.value.filter((p) => !p.is_yield);
        });

        // Get selected LTBA publisher from store
        const selectedPublisher = computed(() => store.getters[getterTypes.getSelectedPublisherLtba]);

        // Model for v-model binding - just return the selected publisher
        const selectedPublisherModel = computed({
            get() {
                return selectedPublisher.value;
            },
            set(value) {
                if (value && value.id) {
                    publisherSelected(value);
                }
            },
        });

        // Handle publisher selection
        const publisherSelected = (publisher) => {
            if (!publisher.id) return;

            store.dispatch(actionTypes.setSelectedPublisherLtba, publisher);
            // Clear website IDs when switching publishers
            store.dispatch(actionTypes.setSelectedWebsitesLtba, []);

            // If current route carries a :publisher param (websites, install,
            // dashboard-ltba, userList, ...), stay on that route and just swap the
            // publisher. Drop :website — it belongs to the previous publisher.
            // Otherwise (fees, ads-txt, users, ...) stay put — views watch the
            // selected publisher and refetch themselves.
            const hasPublisherParam = route.params && "publisher" in route.params;
            if (hasPublisherParam && route.name) {
                const newParams = { ...route.params, publisher: String(publisher.id) };
                delete newParams.website;
                router.push({ name: route.name, params: newParams });
            } else if (!route.name) {
                // No matched route (shouldn't normally happen) — fall back to dashboard
                router.push(`/dashboard/ltba/${publisher.id}`);
            }
        };

        // Sync with route params
        watch(
            [selectedPublisher, route],
            () => {
                if (route.name === "dashboard-ltba" && !route.params.publisher && selectedPublisher.value) {
                    router
                        .replace({
                            name: "dashboard-ltba",
                            params: { publisher: selectedPublisher.value.id },
                        })
                        .catch(() => {
                            // Ignore navigation duplicated error
                        });
                }
            },
            { immediate: true }
        );

        // Initialize selected publisher on first LTBA visit: only when nothing is
        // selected and the URL carries no :publisher (otherwise the route watcher
        // is the source of truth).
        watch(
            [ltbaPublishers, selectedPublisher, route],
            ([publishers, selected, currentRoute]) => {
                if (
                    publishers?.length > 0 &&
                    !selected &&
                    currentRoute?.name === "dashboard-ltba" &&
                    !currentRoute?.params?.publisher
                ) {
                    store.dispatch(actionTypes.setSelectedPublisherLtba, publishers[0]);
                    store.dispatch(actionTypes.setSelectedMode, "ltba");
                }
            },
            { immediate: true }
        );

        return {
            ltbaPublishers,
            selectedPublisherModel,
            publisherSelected,
        };
    },
};
</script>
